import React, { useEffect, useRef, useState } from 'react'
import './styles.scss'

import * as d3Scale from 'd3-scale'
import * as d3Selection from 'd3-selection'
import * as d3Array from 'd3-array'
import * as d3Multi from 'd3-selection-multi'
import * as d3Axis from 'd3-axis'
import * as d3TimeFormat from 'd3-time-format'
import * as d3Time from 'd3-time'
import * as d3Shape from 'd3-shape'
// const scaleSteps = 250

const isMobile = window.innerWidth <= 599
const d3 = {
  ...d3Scale,
  ...d3Selection,
  ...d3Array,
  ...d3Multi,
  ...d3Axis,
  ...d3TimeFormat,
  ...d3Time,
  ...d3Shape
}

export const CaseChart = ({
  data,
  prop,
  aveprop,
  scaleSteps,
  domain,
  showAverage,
  vertflip,
  className
}: {
  data: chartData[]
  prop: string
  aveprop: string
  scaleSteps: number
  domain: [number, number] | number[]
  showAverage: boolean
  vertflip: boolean
  className: string
}) => {
  const svgRef = useRef(null)
  const [svgSize, setSvgSize] = useState<[number, number] | undefined>(
    undefined
  )

  const padding = [vertflip ? 0 : 8, 10, 16, 10]

  const draw = () => {
    if (!data || !svgSize) return
    const movingAverage = data
      .filter(f => f[aveprop])
      .map(m => {
        return { x: m.date, y: +m[aveprop] }
      })

    const dateExtend = data.map(d => new Date(d.date))

    const tickDates = dateExtend.filter((value: Date, index: number) => {
      return isMobile ? index % 7 == 0 : index % 6 == 0
    })
    tickDates.pop()
    tickDates.push(new Date(dateExtend[dateExtend.length - 1]))

    const line = d3
      .line<{ x: string; y: number }>()
      .x(d => padding[3] + x(new Date(d.x)) + x.bandwidth() / 2)
      .y(d => y(d.y) + padding[0])
      .curve(d3.curveLinear)

    const svg = d3
      .select(svgRef.current)
      .html('')
      .attr('viewBox', '0 0 ' + svgSize[0] + ' ' + svgSize[1])

    const x = d3
      .scaleBand<Date>()
      .domain(dateExtend)
      .range([padding[3], svgSize[0] - padding[1] - padding[3]])
      .paddingInner(0.25)

    const xAxis = d3
      .axisBottom(x)
      .tickFormat(d3.timeFormat('%-d.%-m'))
      .tickSizeOuter(0)
      .tickSize(0)
      .tickValues(tickDates.reverse())

    const y = d3
      .scaleLinear()
      .domain(vertflip ? [domain[1], domain[0]] : domain)
      .range([svgSize[1] - padding[0] - padding[2], padding[0]])

    const yAxis = d3
      .axisLeft(y)
      .tickSizeOuter(0)
      .tickSizeInner(-svgSize[0])
      .tickValues(
        d3.range(
          0,
          vertflip ? y.domain()[0] + scaleSteps : y.domain()[1] + scaleSteps,
          scaleSteps
        )
      )

    if (!vertflip) {
    const xaxis = svg
      .append('g')
      .attr('class', 'xaxis')
      .attr(
        'transform',
        'translate(' +
          x(x.domain()[0]) +
          ',' +
          (vertflip ? 0 : svgSize[1] - padding[2]) +
          ')'
      )
      .call(xAxis)

    const xTicks = xaxis.selectAll('text')
    xTicks.attr('transform', 'translate(0,5)').each((d, e, f) => {
      const step = Math.ceil(svgSize[0] / (f.length * x.bandwidth()))
      if (e === 0) d3.select(f[e]).attr('text-anchor', 'end')
      if (e % step !== 0) d3.select(f[e]).remove()
    })
    xaxis.selectAll('.domain').remove()
    }

    const yaxis = svg
      .append('g')
      .attr('class', 'yaxis')
      .attr('transform', 'translate(' + 3 + ',' + padding[0] + ')')
      .call(yAxis)

    yaxis.selectAll('.domain').remove()
    yaxis.selectAll('line').attr('stroke', '#ddd')
    yaxis
      .selectAll('text')
      .attr('transform', 'translate(0,-8)')
      .style('text-anchor', 'start')
      .style('fill', '#999BAA')

    svg
      .append('g')
      .attr('class', 'bars')
      .selectAll('rect')
      .data(data)
      .join('rect')
      .attrs({
        x: d => {
          return padding[3] + x(new Date(d.date))
        },
        y: d => {
          return vertflip ? y(0) + padding[0] : y(+d[prop]) + padding[0]
        },
        width: x.bandwidth(),
        height: d => {
          return vertflip
            ? y(+d[prop]) - padding[0]
            : y(y.domain()[0]) - y(+d[prop])
        },
        class: d => {
          return prop + ' ' + d.date
        },
        style: 'cursor:pointer'
      })
      .on('mouseover click', (d, e, f) => {
        const el = d3.select(
          f.filter(g => {
            if (g.classList.contains(d.date)) return f
          })[0]
        )

        el.attr('stroke', '#000')

        tt.attr(
          'transform',
          'translate(' +
            (+el.attr('x') + x.bandwidth() / 2) +
            ',' +
            (vertflip
              ? +el.attr('y') + +el.attr('height')
              : +el.attr('y') - 25) +
            ')'
        )
        ttText.text(d[prop])
      })
      .on('mouseout', (d, e, f) => {
        const el = f.filter(g => {
          if (g.classList.contains(d.date)) return f
        })[0]
        d3.select(el).attr('stroke', 'none')
        tt.attr('transform', 'translate(-100,-100)')
      })
    if (showAverage) {
      svg
        .append('g')
        .attr('class', 'ave')
        .append('path')
        .attrs({
          d: line(movingAverage),
          class: 'movave ' + prop
        })
    }
    const tt = svg
      .append('g')
      .attr('transform', 'translate(-100,-100)')
      .attr('class', 'tt')
    const ttText = tt
      .append('text')
      .attrs({ x: 0, y: 18, 'text-anchor': 'middle', fill: '#999BAA' })
      .text('234')

    const lastVal = data[data.length - 1]
    if (+lastVal[prop] > 0) {
      const lastValue = svg.append('g').attr('class', 'lastV')
      lastValue
        .append('text')
        .attrs({
          x: padding[3] + x(new Date(lastVal.date)) + x.bandwidth(),
          y: vertflip ? y(+lastVal[prop]) + 70 : y(+lastVal[prop]) - 20,
          class: 'lastVal ' + prop,
          'text-anchor': 'end'
        })
        .text(lastVal[prop])
      lastValue.append('line').attrs({
        x1: padding[3] + x(new Date(lastVal.date)) + x.bandwidth() / 2,
        x2: padding[3] + x(new Date(lastVal.date)) + x.bandwidth() / 2,
        y1: vertflip ? y(+lastVal[prop]) + 55 : y(+lastVal[prop]) - 16,
        y2: vertflip ? y(+lastVal[prop]) + 20 : y(+lastVal[prop]) + 0,
        class: 'lastValLine'
      })
    }
  }
  useEffect(() => {
    if (svgRef && svgRef.current) {
      const width = svgRef.current.clientWidth || 400
      const height = svgRef.current.clientHeight || 200
      setSvgSize([width, height])
    }
    draw()
  }, [svgRef.current])
  return <svg className={className} ref={svgRef} />
}
